import { useEffect, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import mermaid from 'mermaid';

/**
 * Renders a Mermaid diagram from source text. Re-renders on theme change so the
 * diagram tracks light/dark mode. Uses securityLevel 'strict' — Mermaid escapes
 * node/edge text, so backend names from the catalog are rendered as plain text.
 */
export function MermaidDiagram({ chart }: { chart: string }) {
  const theme = useTheme();
  const [svg, setSvg] = useState('');
  const [error, setError] = useState<string | undefined>();
  // useId() is stable across renders and SSR-safe; strip ':' which is invalid
  // in DOM ids / CSS selectors that Mermaid uses internally.
  const id = `mermaid-${useId().replace(/:/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: theme.palette.mode === 'dark' ? 'dark' : 'default',
      flowchart: { htmlLabels: false, useMaxWidth: true },
    });
    mermaid
      .render(id, chart)
      .then(({ svg: rendered }) => {
        if (!cancelled) {
          setSvg(rendered);
          setError(undefined);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chart, id, theme.palette.mode]);

  if (error) {
    return (
      <Box>
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Could not render diagram: {error}
        </Typography>
        <Box component="pre" sx={{ overflowX: 'auto', fontSize: 12 }}>
          {chart}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{ '& svg': { maxWidth: '100%', height: 'auto' } }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
